/**
 * Document RTL for Hebrew: section `pageSetup.sectionDirection` when available,
 * plus paragraph-level RLM (U+200F) fallback when the host rejects PageSetup (e.g. some Word Online paths).
 */

/* global Word */

import { log } from '../../utils/logging';
import { formatOfficeJsError } from '../../utils/officeErrorFormat';

/** Hebrew block in Unicode */
const HEBREW_RE = /[\u0590-\u05FF]/;

/** Skip if paragraph already starts with explicit bidi marks */
const LEADING_BIDI_MARK = /^[\u200E\u200F\u202A-\u202E\u2066-\u2069]/;

const RLM = '\u200F';

export function textLooksHebrew(text: string): boolean {
  return HEBREW_RE.test(text);
}

/**
 * Set first section to RTL via `sections.getFirst().pageSetup`, then `document.pageSetup`.
 * @returns whether section-level RTL was applied successfully.
 */
export async function trySetSectionRtl(context: Word.RequestContext): Promise<boolean> {
  try {
    const first = context.document.sections.getFirst();
    first.load('pageSetup');
    await context.sync();
    first.pageSetup.sectionDirection = Word.SectionDirection.rightToLeft;
    await context.sync();
    log('[wordDocumentRtl] sections.getFirst().pageSetup.sectionDirection = RightToLeft');
    return true;
  } catch (e: unknown) {
    log('[wordDocumentRtl] section via sections.getFirst() failed', formatOfficeJsError(e));
  }
  try {
    const doc = context.document;
    doc.load('pageSetup');
    await context.sync();
    doc.pageSetup.sectionDirection = Word.SectionDirection.rightToLeft;
    await context.sync();
    log('[wordDocumentRtl] document.pageSetup.sectionDirection = RightToLeft');
    return true;
  } catch (e: unknown) {
    log('[wordDocumentRtl] document.pageSetup.sectionDirection failed', formatOfficeJsError(e));
  }
  return false;
}

function paragraphNeedsRlm(text: string): boolean {
  if (!textLooksHebrew(text)) return false;
  const t = text.trimStart();
  if (!t) return false;
  return !LEADING_BIDI_MARK.test(t);
}

/**
 * Hosts that block PageSetup still lay out list markers on the wrong side; prepending RLM
 * to each Hebrew paragraph nudges Word toward RTL paragraph direction for that run.
 */
export async function prependRlmToHebrewParagraphs(context: Word.RequestContext): Promise<number> {
  let count = 0;
  try {
    const body = context.document.body;
    const pc = body.paragraphs;
    pc.load('items');
    await context.sync();
    const items = pc.items as any[];
    for (let i = 0; i < items.length; i++) {
      items[i].load('text');
    }
    await context.sync();
    for (let i = 0; i < items.length; i++) {
      const text = String(items[i].text ?? '');
      if (!paragraphNeedsRlm(text)) continue;
      items[i].getRange().insertText(RLM, Word.InsertLocation.start);
      count += 1;
    }
    if (count > 0) {
      await context.sync();
    }
    log('[wordDocumentRtl] RLM fallback applied', { paragraphCount: count });
  } catch (e: unknown) {
    log('[wordDocumentRtl] RLM fallback failed', formatOfficeJsError(e));
  }
  return count;
}

export type RtlApplyResult = {
  sectionRtl: boolean;
  rlmParagraphs: number;
};

/**
 * Try section RTL; if it fails, prepend RLM on Hebrew paragraphs.
 */
export async function ensureRtlForHebrewDocument(
  context: Word.RequestContext,
  textSample: string
): Promise<RtlApplyResult> {
  if (!textLooksHebrew(textSample)) {
    return { sectionRtl: false, rlmParagraphs: 0 };
  }
  const sectionRtl = await trySetSectionRtl(context);
  const rlmParagraphs = sectionRtl ? 0 : await prependRlmToHebrewParagraphs(context);
  return { sectionRtl, rlmParagraphs };
}

/** Legacy name: section RTL only (no RLM). Prefer `trySetSectionRtl` + end-of-run RLM in inserter. */
export async function ensureSectionRtlIfHebrew(
  context: Word.RequestContext,
  textSample: string
): Promise<void> {
  if (!textLooksHebrew(textSample)) return;
  await trySetSectionRtl(context);
}

/**
 * Full RTL pass for agent validation after first insert (retry section + RLM fallback).
 */
export async function validateDocumentRtl(): Promise<{
  ok: boolean;
  sectionRtl: boolean;
  rlmParagraphs: number;
  notHebrew?: boolean;
  detail?: string;
}> {
  try {
    let result: RtlApplyResult = { sectionRtl: false, rlmParagraphs: 0 };
    let notHebrew = false;
    await Word.run(async (context) => {
      const body = context.document.body;
      body.load('text');
      await context.sync();
      const sample = String(body.text ?? '');
      if (!textLooksHebrew(sample)) {
        notHebrew = true;
        return;
      }
      result = await ensureRtlForHebrewDocument(context, sample);
    });
    if (notHebrew) {
      return { ok: true, sectionRtl: false, rlmParagraphs: 0, notHebrew: true };
    }
    const ok = result.sectionRtl || result.rlmParagraphs > 0;
    return {
      ok,
      sectionRtl: result.sectionRtl,
      rlmParagraphs: result.rlmParagraphs,
      detail: ok
        ? undefined
        : 'Could not set section RTL or apply RLM markers; open in Word desktop or set paragraph direction manually.',
    };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    return {
      ok: false,
      sectionRtl: false,
      rlmParagraphs: 0,
      detail: msg,
    };
  }
}
